import {
  ButtonDS,
  DeleteOutlined,
  LaunchPage,
  MaterialUiColumnsProps,
  MaterialUiTable,
  SelectDS,
  Spacer,
  TextCapitalized,
  triggerToast,
  usePersistMuiTableState,
} from "@qivia/ui";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import styled from "styled-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  DataGridPremiumProps,
  GRID_AGGREGATION_ROOT_FOOTER_ROW_ID,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { useTranslation } from "react-i18next";
import "../../../i18n/i18n";
import {
  companiesListAsync,
  selectCompaniesList,
  selectCompaniesListStatus,
} from "../../companiesSlice";
import { companyAccountDataAsync } from "../../companiesData/accountDataSlice";
import { Vehicle, VehicleDisplayed } from "../telemetryApi";
import { TelemetryDataProvider } from "@qivia/schemas-telemetry";
import { formatDateWithHourMinute } from "../../../lib/materialUiFormats";
import { UpdateDeviceIdCell } from "../components/updateDeviceIdCell";
import { selectAdminUser } from "../../homeSlice";
import {
  disconnectVehicleFromProviderAsync,
  selectDisconnectVehicleFromProviderStatus,
  telemetrySlice,
} from "../telemetrySlice";
import { DisconnectVehicleFromProviderModal } from "../components/disconnectVehicleFromProviderModal";

type VehiclesTableProps = {
  vehiclesListWithId: (VehicleDisplayed & { id: string })[];
  pageName: string;
  pageTitle: string;
  action?: React.ReactNode;
  rowsChecked?: VehicleDisplayed[];
  setRowsChecked?: (rows: VehicleDisplayed[]) => void;
  canEditDeviceId?: boolean;
  canDisconnectVehicle?: boolean;
  dataProvider?: TelemetryDataProvider;
} & Omit<DataGridPremiumProps, "rows" | "columns">;

export const VehiclesTable = ({
  vehiclesListWithId,
  pageName,
  pageTitle,
  action,
  rowsChecked,
  setRowsChecked,
  canEditDeviceId,
  canDisconnectVehicle,
  dataProvider,
  loading,
}: VehiclesTableProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const params = useParams();
  const companiesList = useAppSelector(selectCompaniesList);
  const companiesListStatus = useAppSelector(selectCompaniesListStatus);
  const adminUser = useAppSelector(selectAdminUser);
  const disconnectVehicleFromProviderStatus = useAppSelector(
    selectDisconnectVehicleFromProviderStatus,
  );
  const [listDisplayed, setListDisplayed] = useState<VehicleDisplayed[]>([]);
  const [companySelected, setCompanySelected] = useState<string>("");
  const [vehicleToDisconnect, setVehicleToDisconnect] =
    useState<Vehicle | null>(null);

  const optionCompany = useMemo(
    () =>
      companiesList.map((company) => {
        return {
          label: company.name,
          value: company.uuid,
        };
      }),
    [companiesList],
  );

  const valueOptionCompany = useMemo(
    () => optionCompany.find((option) => option.value === companySelected),
    [companySelected, optionCompany],
  );

  const apiRef = useGridApiRef();

  const aggregation = {
    model: {
      registrationNumber: "size",
      identifier: "size",
      isEligible: "size",
      connectionStatus: "size",
      geolocationError: "size",
    },
  };

  const handleDisconnectVehicleFromProvider = useCallback(() => {
    if (!vehicleToDisconnect?.uuid) return;
    if (!adminUser?.email) return;

    void dispatch(
      disconnectVehicleFromProviderAsync({
        vehicleUuid: vehicleToDisconnect.uuid,
        updatedBy: adminUser.email,
      }),
    );
    setVehicleToDisconnect(null);
  }, [dispatch, vehicleToDisconnect?.uuid, adminUser?.email]);

  useEffect(() => {
    if (disconnectVehicleFromProviderStatus === "success") {
      const timer = setTimeout(() => {
        triggerToast(
          t("telemetry.disconnectVehicleFromProvider.success") || "",
          "valid",
        );
        void dispatch(
          telemetrySlice.actions.refreshAnalyticVehiclesListOfACompany(),
        );
        void dispatch(
          telemetrySlice.actions.resetDisconnectVehicleFromProviderStatus(),
        );
      }, 2000);
      return () => clearTimeout(timer);
    } else if (disconnectVehicleFromProviderStatus === "failed") {
      triggerToast(
        t("telemetry.disconnectVehicleFromProvider.failure") || "",
        "error",
      );
      dispatch(
        telemetrySlice.actions.resetDisconnectVehicleFromProviderStatus(),
      );
    }
  }, [disconnectVehicleFromProviderStatus, t, dispatch]);

  const dataGridState = usePersistMuiTableState(
    apiRef,
    pageTitle,
    pageName,
    aggregation,
  );

  const authorizedAdmin = useMemo(() => {
    return [
      "p.chopin@qivia.fr",
      "m.gatinois@qivia.fr",
      "m.banjac@qivia.fr",
      "a.chagra@qivia.fr",
      "a.em@qivia.fr",
    ].includes(adminUser?.email ?? "");
  }, [adminUser]);

  const searchBarProps = {
    values: vehiclesListWithId,
    setFilterValues: setListDisplayed,
    keysToIgnore: ["uuid" as const, "deliveryDate" as const],
  };

  const columnsDisplayed: {
    [index in keyof VehicleDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    const allColumns: Partial<{ [index in keyof VehicleDisplayed]: MaterialUiColumnsProps }> = {
      registrationNumber: {
        type: "string",
        width: 200,
      },
      identifier: {
        type: "string",
        width: 200,
      },
      deviceId: {
        type: "string",
        width: 200,
        renderCell: (props) => {
          if (props.id === GRID_AGGREGATION_ROOT_FOOTER_ROW_ID) return;
          if (canEditDeviceId) {
            return <UpdateDeviceIdCell cellProps={props} />;
          }

          return props.value as string;
        },
      },
      isEligible: {
        type: "string",
        width: 200,
        valueFormatter: (value: string | null) => (
          <TextCapitalized>{value}</TextCapitalized>
        ),
      },
      connectionStatus: {
        type: "string",
        width: 200,
        valueFormatter: (value: string | null) => (
          <TextCapitalized>{value}</TextCapitalized>
        ),
      },
      dataProvider: {
        type: "string",
        width: 200,
        valueFormatter: (value: string | null) => {
          return (
            <TextCapitalized>
              {value === null || value === undefined
                ? t("vehiclesOfACompany.column.null")
                : value}
            </TextCapitalized>
          );
        },
      },
      connectionPlan: {
        type: "string",
        valueFormatter: (value: string | null) => {
          return (
            <TextCapitalized>{value}</TextCapitalized>
          );
        },
      },
      geolocationError: {
        type: "string",
        width: 200,
        valueFormatter: (value: string | null) => {
          return (
            <TextCapitalized>
              {value === null || value === undefined ? "" : value}
            </TextCapitalized>
          );
        },
      },
      brand: {
        type: "string",
        width: 200,
      },
      createdAt: {
        type: "string",
        width: 200,
        valueFormatter: (value: string | null) => {
          return value ? formatDateWithHourMinute(value) : null;
        },
      },
      requestedConnectionAt: {
        type: "string",
        width: 200,
        valueFormatter: (value: string | null) => {
          return value ? formatDateWithHourMinute(value) : null;
        },
      },
      deviceInstalledAt: {
        type: "string",
        width: 200,
        valueFormatter: (value: string | null) => {
          return value ? formatDateWithHourMinute(value) : null;
        },
      },
      department: {
        type: "string",
        width: 200,
      },
      connectionRevokedAt: {
        type: "string",
        width: 150,
        renderCell: (props) => {
          if (props.id === GRID_AGGREGATION_ROOT_FOOTER_ROW_ID) return;
          const row = props.row as VehicleDisplayed;

          const isConnectedOrPending =
            row.connectionStatus ===
              t("vehiclesOfACompany.column.connectionStatus.APPROVED") ||
            row.connectionStatus ===
              t("vehiclesOfACompany.column.connectionStatus.PENDING");

          const isRevoked =
            row.connectionStatus ===
              t("vehiclesOfACompany.column.connectionStatus.REVOKED") &&
            !!row.connectionRevokedAt;

          if (!authorizedAdmin || (isRevoked && row.connectionRevokedAt)) {
            return row.connectionRevokedAt
              ? formatDateWithHourMinute(row.connectionRevokedAt)
              : null;
          }

          return (
            <StyledColumnButton>
              <ButtonDS
                singleIcon={{
                  icon: <DeleteOutlined />,
                  size: "L",
                }}
                buttonType="danger"
                format={"fill"}
                onClick={() => {
                  if (!canDisconnectVehicle || !isConnectedOrPending) return;

                  setVehicleToDisconnect(row as unknown as Vehicle);
                }}
                disabled={!canDisconnectVehicle || !isConnectedOrPending}
              />
            </StyledColumnButton>
          );
        },
      },
    };

    if (dataProvider === "HIGH_MOBILITY") {
      delete allColumns.deviceId;
      delete allColumns.deviceInstalledAt;
    } else if (dataProvider === "FLESPI") {
      delete allColumns.isEligible;
      delete allColumns.connectionPlan;
      delete allColumns.connectionRevokedAt;
    }

    return allColumns as { [index in keyof VehicleDisplayed]: MaterialUiColumnsProps };
  }, [t, canEditDeviceId, canDisconnectVehicle, authorizedAdmin, dataProvider]);

  useEffect(() => {
    if (companiesListStatus === "idle") {
      void dispatch(companiesListAsync());
    }
  }, [companiesListStatus, dispatch]);

  useEffect(() => {
    if (params.id && !companySelected) {
      setCompanySelected(params.id);
    }
  }, [companySelected, params.id]);

  useEffect(() => {
    if (
      vehiclesListWithId &&
      companiesList &&
      companiesListStatus === "success"
    ) {
      setListDisplayed(vehiclesListWithId);
    }
  }, [vehiclesListWithId, companiesList, companiesListStatus]);

  useEffect(() => {
    if (companySelected) {
      void dispatch(companyAccountDataAsync({ companyUuid: companySelected }));
      navigate(`/home/telemetry/${params.tab}/${companySelected}`);
    }
  }, [companySelected, dispatch, navigate, params.tab]);

  if (!listDisplayed) return <LaunchPage hasBorderRadius={true} />;

  return (
    <StyledContainer>
      <Spacer y={1} />
      <StyledHeader>
        <TextCapitalized>{t("accountData.select.company")}</TextCapitalized>
        <SelectDS
          label={""}
          value={valueOptionCompany}
          options={optionCompany}
          onChange={(selectedOption) =>
            setCompanySelected(selectedOption ? selectedOption.value : "")
          }
          allWidth
        />
      </StyledHeader>
      {action && (
        <>
          <Spacer y={1} />
          {action}
          <Spacer y={1} />
        </>
      )}
      <MaterialUiTable<keyof VehicleDisplayed, VehicleDisplayed>
        rowsChecked={rowsChecked}
        setRowsChecked={setRowsChecked}
        rows={listDisplayed}
        columns={columnsDisplayed}
        pageName={pageName}
        dataGridState={dataGridState}
        searchBar={searchBarProps}
        apiRef={apiRef}
        loading={loading}
      />
      <DisconnectVehicleFromProviderModal
        visible={!!vehicleToDisconnect}
        registrationNumber={vehicleToDisconnect?.registrationNumber ?? ""}
        onClose={() => setVehicleToDisconnect(null)}
        onConfirm={handleDisconnectVehicleFromProvider}
      />
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StyledHeader = styled.div`
  display: flex;
  flex-direction: column;
  width: 30rem;
  z-index: 10;
`;

const StyledColumnButton = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
`;
